import { useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react';
import { useToast, Toast as ToastType } from '../../contexts/ToastContext';

const typeConfig: Record<ToastType['type'], {
  icon: typeof CheckCircle;
  bgClass: string;
  iconClass: string;
  textClass: string;
}> = {
  info: {
    icon: Info,
    bgClass: 'bg-white dark:bg-surface-elevated border-border',
    iconClass: 'text-primary-500 dark:text-primary-400',
    textClass: 'text-text-primary',
  },
  success: {
    icon: CheckCircle,
    bgClass: 'bg-white dark:bg-surface-elevated border-success-200 dark:border-success-800',
    iconClass: 'text-success-500 dark:text-success-400',
    textClass: 'text-text-primary',
  },
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-white dark:bg-surface-elevated border-warning-200 dark:border-warning-800',
    iconClass: 'text-warning-500 dark:text-warning-400',
    textClass: 'text-text-primary',
  },
  error: {
    icon: AlertCircle,
    bgClass: 'bg-white dark:bg-surface-elevated border-error-200 dark:border-error-800',
    iconClass: 'text-error-500 dark:text-error-400',
    textClass: 'text-text-primary',
  },
  loading: {
    icon: Loader2,
    bgClass: 'bg-white dark:bg-surface-elevated border-border',
    iconClass: 'text-primary-500 dark:text-primary-400 animate-spin',
    textClass: 'text-text-primary',
  },
};

interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);
  const config = typeConfig[toast.type];
  const Icon = config.icon;

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  const handleAction = () => {
    if (toast.onAction) {
      toast.onAction();
    }
    handleDismiss();
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-3 rounded-lg border shadow-lg
        transition-all duration-200 ease-out
        ${config.bgClass}
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
      `}
      role="alert"
    >
      <Icon className={`w-5 h-5 flex-shrink-0 mt-1 ${config.iconClass}`} />

      <div className="flex-1 min-w-0">
        <p className={`text-sm ${config.textClass}`}>
          {toast.message}
        </p>

        {(toast.actionLabel && (toast.actionHref || toast.onAction)) && (
          toast.actionHref ? (
            <a
              href={toast.actionHref}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-1 inline-block"
              onClick={handleDismiss}
            >
              {toast.actionLabel}
            </a>
          ) : (
            <button
              onClick={handleAction}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mt-1"
            >
              {toast.actionLabel}
            </button>
          )
        )}
      </div>

      {toast.type !== 'loading' && (
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-surface-elevated transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-text-muted" />
        </button>
      )}
    </div>
  );
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={removeToast} />
        </div>
      ))}
    </div>
  );
}

export { ToastItem };
